/**
 * The routes this build renders to a static document of their own, and the
 * head each one gets. Every title and description here is public copy and a
 * draft for Russ's review (docs/sprints/web-perf/home-document.md).
 *
 * Both are landing pages whose LCP is text the build can put in the HTML, so
 * it paints during the bundle download instead of after it:
 * `/for-teachers/odyssey` is where the paid traffic lands, and `/` paints its
 * masthead while the shelf's covers load. A route only prerenders to
 * something worth having if its component is in the entry chunk (a `lazy()`
 * route renders its Suspense fallback into the document), which is why
 * App.tsx keeps these two eager.
 *
 * OUTPUT NAMES ARE LOAD-BEARING — see `outputFileFor`.
 */
import {
  outputFileFor,
  titleFor as titleIn,
  type PrerenderRoute,
} from "@bindery/prerender/route";

/** The document title for a path this manifest does not name. */
export const DEFAULT_TITLE = "Retells — classic works retold, side by side";

/**
 * Where a route's document is written: `outputFileFor` from
 * @bindery/prerender/route, which writes `/foo` to a flat `foo.html` (the
 * package has the asset worker's resolution order and why a directory index
 * would cost a redirect). public/sw.js and scripts/sw-assets.ts key pages by
 * the same flat spelling.
 */
export { outputFileFor };

/**
 * `/`'s document. Not `outputFileFor("/")`: index.html is also what
 * `not_found_handling: "single-page-application"` and public/_redirects
 * serve every client route, and the shelf's masthead prerendered into it
 * would paint in front of every reader deep link before React threw it
 * away. public/_redirects rewrites `/` alone onto this file.
 */
export const HOME_DOCUMENT = "home.html";

export const PRERENDER_ROUTES: readonly PrerenderRoute[] = [
  // First: scripts/sw-assets.ts precaches these in order, and public/sw.js
  // falls back to the first one offline.
  {
    path: "/",
    outputFile: HOME_DOCUMENT,
    title: DEFAULT_TITLE,
    // The masthead's tagline, word for word (src/reader/library-page.tsx).
    description:
      "Classic works retold in plain, modern prose — each retelling synced line-for-line against its original.",
    mustContain: "Original texts are in the public domain",
  },
  {
    path: "/for-teachers/odyssey",
    outputFile: outputFileFor("/for-teachers/odyssey"),
    title: "The Odyssey for the classroom — Retells",
    description:
      "A translation, not an abridgment: all twenty-four books of the Odyssey in plain modern English, set line-for-line beside Homer's Greek, with tap-for-background notes on its names, places and customs from Simple English Wikipedia.",
    // teacher-page.tsx:271, the page's opening claim — prose a visitor reads,
    // not chrome. Deliberately NOT the `<h1>` and NOT this `description`:
    // matching the description would pass off the `<meta>` tag the pass writes
    // itself, which proves nothing about the render.
    mustContain: "This is a translation, not an abridgment",
  },
];

/** The title for `path`, falling back to the site-wide one. */
export function titleFor(path: string): string {
  return titleIn(PRERENDER_ROUTES, path, DEFAULT_TITLE);
}
